import { useEffect, useRef, useState } from 'react';
import { suggestEvents, isAiHelpConfigured } from './api';

/**
 * Debounced Jules event suggestions.
 * @param {object} params
 * @param {boolean} params.enabled
 * @param {string} params.queryName
 * @param {string} [params.dateStart]
 * @param {string} [params.dateEnd]
 * @param {object} params.system
 * @param {number} params.numZones
 * @param {'scene'|'schedule'} params.context
 * @param {number} [params.delayMs]
 */
export function useAiEventSuggestions({
	enabled,
	queryName,
	dateStart,
	dateEnd,
	system,
	numZones = 4,
	context = 'scene',
	delayMs = 600,
}) {
	const [state, setState] = useState({
		loading: false,
		error: null,
		events: null,
		count: 0,
	});
	const reqId = useRef(0);

	useEffect(() => {
		if (!enabled || !isAiHelpConfigured() || !system?.uid) {
			setState({ loading: false, error: null, events: null, count: 0 });
			return undefined;
		}

		const name = (queryName || '').trim();
		if (name.length < 2) {
			setState({ loading: false, error: null, events: null, count: 0 });
			return undefined;
		}

		const timer = setTimeout(async () => {
			const id = ++reqId.current;
			setState((prev) => ({ ...prev, loading: true, error: null }));
			try {
				const data = await suggestEvents({
					systemId: system.uid,
					queryName: name,
					dateStart: dateStart || undefined,
					dateEnd: dateEnd || dateStart || undefined,
					timezone: system.timezone,
					latitude: system.latitude,
					longitude: system.longitude,
					locationLabel: system.location || '',
					numZones,
					context,
				});
				if (id !== reqId.current) return;
				setState({
					loading: false,
					error: null,
					events: data.events || null,
					count: data.count || 0,
				});
			} catch (err) {
				if (id !== reqId.current) return;
				setState({
					loading: false,
					error: err.message || 'Suggestion failed',
					events: null,
					count: 0,
				});
			}
		}, delayMs);

		return () => clearTimeout(timer);
	}, [
		enabled,
		queryName,
		dateStart,
		dateEnd,
		system?.uid,
		system?.timezone,
		system?.latitude,
		system?.longitude,
		system?.location,
		numZones,
		context,
		delayMs,
	]);

	return state;
}
